// Карманные деньги: кошелёк ребёнка, траты и подарки, копилки из кошелька; у взрослых — выдать и карманные по расписанию.
import { tipOfWeek, weeklyPace, weeksToGoal, type GoalDto, type PocketAllowanceDto, type PocketChildDto, type PocketEntryDto, type PocketWalletDto } from "@home/shared";
import { useState, type ReactNode } from "react";
import { api } from "../api.ts";
import { parseAmount } from "../amount.ts";
import { dayTitle, money, todayIso } from "../format.ts";
import { t, type Lang } from "../i18n.ts";
import { errorText } from "../meta.ts";
import { confirmDialog, hapticSelect, hapticTap } from "../telegram.ts";
import { invalidateRemote, useRemote } from "../useRemote.ts";
import { AmountField } from "./AmountField.tsx";
import { EmptyState } from "./EmptyState.tsx";
import { ContributeSheet, GoalList, NewGoalSheet } from "./Goals.tsx";
import { Segmented } from "./Segmented.tsx";
import { PrimaryAction, Sheet } from "./Sheet.tsx";
import { toast } from "./Toast.tsx";

/** Быстрые суммы ребёнка и взрослого. */
const KID_QUICK = [2_000, 5_000, 10_000, 20_000];
const GIVE_QUICK = [10_000, 20_000, 50_000, 100_000];
/** Сколько записей истории видно сразу. */
const HISTORY_TOP = 10;

const ENTRY_ICONS: Record<PocketEntryDto["kind"], string> = { give: "💰", gift: "🎁", spend: "🛍", save: "🐷" };

function weekdayEvery(weekday: number): string {
  return t("weekdaysEvery").split(",")[weekday - 1] ?? "";
}

function weekdayShort(weekday: number): string {
  return t("weekdaysShort").split(",")[weekday - 1] ?? String(weekday);
}

/** «20 000 сум по субботам · следующие — завтра». */
function allowanceSchedule(a: PocketAllowanceDto, lang: Lang): string {
  return `${money(a.amount, "UZS", lang)} ${weekdayEvery(a.weekday)} · ${t("allowanceNext", { date: dayTitle(a.nextDate, lang).toLowerCase() })}`;
}

/** «Карманные: 20 000 сум по субботам» и, если нужно, когда следующие. */
export function allowanceText(a: PocketAllowanceDto, lang: Lang, next = true): string {
  const every = t("allowanceEvery", { amount: money(a.amount, "UZS", lang), weekday: weekdayEvery(a.weekday) });
  return next ? `${every} · ${t("allowanceNext", { date: dayTitle(a.nextDate, lang).toLowerCase() })}` : every;
}

function entryTitle(e: PocketEntryDto): string {
  // У «копилки» сумма — изменение кошелька: минус — отложил, плюс — взял обратно.
  if (e.kind === "save") return t(e.amount < 0 ? "pocket.saveIn" : "pocket.saveOut", { name: e.goalName ?? "" });
  return e.note ?? t(`pocket.${e.kind}`);
}

/** Остаток кошелька, карманные и итоги месяца. children — кнопки действий под суммой. */
export function WalletHero({ wallet, lang, inSheet = false, children }: { wallet: PocketWalletDto; lang: Lang; inSheet?: boolean; children?: ReactNode }) {
  const { got, spent, saved } = wallet.month;
  return (
    <section className={inSheet ? "pocket-summary" : "dash-card home-hero"}>
      <div className="hero-label">{t("walletTitle")}</div>
      <div className={wallet.balance < 0 ? "hero-value negative" : "hero-value"}>{money(wallet.balance, "UZS", lang)}</div>
      {wallet.allowance && !inSheet && <div className="hint">{allowanceText(wallet.allowance, lang)}</div>}
      {children}
      {(got > 0 || spent > 0 || saved > 0) && (
        <dl className="pocket-month">
          <dt>{t("thisMonth")}</dt>
          <div>
            <dd>{t("gotL")}</dd>
            <b className={got > 0 ? "income" : ""}>{money(got, "UZS", lang, true)}</b>
          </div>
          <div>
            <dd>{t("spentL")}</dd>
            <b>{money(spent, "UZS", lang)}</b>
          </div>
          <div>
            <dd>{t("savedL")}</dd>
            <b>{money(saved, "UZS", lang)}</b>
          </div>
        </dl>
      )}
    </section>
  );
}

/** История кошелька: нажатие на свою запись — удалить. */
export function PocketHistory({ wallet, lang, inSheet = false }: { wallet: PocketWalletDto; lang: Lang; inSheet?: boolean }) {
  const [all, setAll] = useState(false);
  if (wallet.entries.length === 0) return null;
  const shown = all ? wallet.entries : wallet.entries.slice(0, HISTORY_TOP);

  const remove = async (e: PocketEntryDto) => {
    hapticSelect();
    if (!(await confirmDialog(t("confirmDeleteEntry", { what: entryTitle(e) }), t("delete")))) return;
    try {
      await api.deletePocketEntry(e.id);
      hapticTap();
      toast(t("deletedOk"));
      invalidateRemote();
    } catch (err) {
      toast(errorText(err));
    }
  };

  const list = (
    <>
      <ul className="row-list">
        {shown.map((e) => {
          const meta = [dayTitle(e.date, lang), e.note && e.kind !== "save" ? t(`pocket.${e.kind}`) : null, e.byChild ? null : e.authorName]
            .filter(Boolean)
            .join(" · ");
          const body = (
            <>
              <span className="row-icon" aria-hidden="true">
                {ENTRY_ICONS[e.kind]}
              </span>
              <div className="row-body">
                <div className="row-title">{entryTitle(e)}</div>
                <div className="row-meta">{meta}</div>
              </div>
              <div className={e.amount > 0 ? "row-end amount income" : "row-end amount"}>{money(e.amount, "UZS", lang, true)}</div>
            </>
          );
          return (
            <li key={e.id}>
              {e.canDelete ? (
                <button type="button" className="row row-btn" onClick={() => void remove(e)}>
                  {body}
                </button>
              ) : (
                <div className="row">{body}</div>
              )}
            </li>
          );
        })}
      </ul>
      {!all && wallet.entries.length > HISTORY_TOP && (
        <button type="button" className="btn btn-block" onClick={() => setAll(true)}>
          {t("showAllN", { n: wallet.entries.length })}
        </button>
      )}
    </>
  );
  if (inSheet) {
    return (
      <>
        <div className="section-title">{t("pocketHistory")}</div>
        {list}
      </>
    );
  }
  return (
    <section className="dash-card">
      <h3>{t("pocketHistory")}</h3>
      {list}
    </section>
  );
}

/** Подсказка под копилкой: «по 4 000 в неделю — ещё 12 недель». */
function paceHint(wallet: PocketWalletDto, lang: Lang): (g: GoalDto) => string | null {
  const pace = weeklyPace(wallet);
  return (g) => {
    const weeks = g.currency === "UZS" ? weeksToGoal(g.target - g.saved, pace) : null;
    return weeks && pace ? t("goalPace", { amount: money(pace, "UZS", lang), n: weeks }) : null;
  };
}

/** Копилки ребёнка: нажатие — отложить из кошелька или взять обратно. */
export function KidGoalsCard({ wallet, lang, onPick }: { wallet: PocketWalletDto; lang: Lang; onPick: (g: GoalDto) => void }) {
  const [creating, setCreating] = useState(false);
  return (
    <section className="dash-card">
      <div className="dash-card-head">
        <h3>🐷 {t("goalsTab")}</h3>
        {wallet.goals.length > 0 && (
          <button type="button" className="btn-small-ghost" onClick={() => setCreating(true)}>
            ＋ {t("newGoal")}
          </button>
        )}
      </div>
      {wallet.goals.length === 0 ? (
        <EmptyState icon="🐷" title={t("noGoalsYet")} text={t("noGoalsChild")} action={{ label: t("createGoal"), onClick: () => setCreating(true) }} />
      ) : (
        <GoalList goals={wallet.goals} lang={lang} onPick={onPick} hint={paceHint(wallet, lang)} />
      )}
      {creating && <NewGoalSheet lang={lang} onClose={() => setCreating(false)} />}
    </section>
  );
}

/** Совет недели по финансовой грамотности — тот же, что в боте. */
export function TipCard({ lang }: { lang: Lang }) {
  return (
    <section className="dash-card tip-card">
      <h3>💡 {t("tipOfWeek")}</h3>
      <p>{tipOfWeek(todayIso(), lang)}</p>
    </section>
  );
}

/** Трата или «мне дали» — пишет ребёнок (или взрослый за него). */
export function RecordSheet({ childId, kind, lang, onClose }: { childId: string; kind: "spend" | "gift"; lang: Lang; onClose: () => void }) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const spend = kind === "spend";

  const submit = async () => {
    if (busy) return;
    const value = parseAmount(amount);
    if (!value) return setError(t("fillAmount"));
    setBusy(true);
    setError(null);
    try {
      const res = await api.pocketRecord(childId, kind, value, note.trim() || null);
      hapticTap();
      toast(`${t("savedOk")} · ${t("inWallet", { amount: money(res.balance, "UZS", lang) })}`);
      invalidateRemote();
      onClose();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  return (
    <Sheet title={spend ? `💸 ${t("spendTitle")}` : `🎁 ${t("giftTitle")}`} onClose={onClose}>
      <div className="form">
        <AmountField value={amount} onChange={setAmount} currency="UZS" lang={lang} quick={KID_QUICK} />
        <label className="field">
          <span>{spend ? t("spendNote") : t("giftNote")}</span>
          <input
            className="input"
            placeholder={spend ? t("spendNotePh") : t("giftNotePh")}
            value={note}
            maxLength={100}
            onChange={(e) => setNote(e.target.value)}
          />
        </label>
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <PrimaryAction label={t("save")} onClick={() => void submit()} busy={busy} />
      </div>
    </Sheet>
  );
}

/** Отложить из кошелька в копилку или взять обратно. */
export function SaveSheet({ wallet, goal: initial, lang, onClose }: { wallet: PocketWalletDto; goal: GoalDto | null; lang: Lang; onClose: () => void }) {
  const goals = wallet.goals.filter((g) => g.currency === "UZS");
  const [goalId, setGoalId] = useState(initial?.id ?? goals[0]?.id ?? null);
  const [direction, setDirection] = useState<"in" | "out">("in");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const goal = goals.find((g) => g.id === goalId) ?? null;

  const submit = async () => {
    if (busy || !goal) return;
    const value = parseAmount(amount);
    if (!value) return setError(t("fillAmount"));
    setBusy(true);
    setError(null);
    try {
      const res = await api.pocketSave(wallet.childId, goal.id, direction === "in" ? value : -value);
      hapticTap();
      toast(res.achieved && direction === "in" ? t("goalReached") : `${t("savedOk")} · ${t("inWallet", { amount: money(res.balance, "UZS", lang) })}`);
      invalidateRemote();
      onClose();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  const pct = goal && goal.target > 0 ? Math.min(100, Math.round((goal.saved / goal.target) * 100)) : 0;
  return (
    <Sheet title={`🐷 ${t("saveTitle")}`} onClose={onClose}>
      <div className="form">
        <p className="hint">{t("inWallet", { amount: money(wallet.balance, "UZS", lang) })}</p>
        {goals.length > 1 && (
          <div className="field">
            <span>{t("pickGoal")}</span>
            <div className="chips" role="radiogroup" aria-label={t("pickGoal")}>
              {goals.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  role="radio"
                  aria-checked={g.id === goalId}
                  className={g.id === goalId ? "chip active" : "chip"}
                  onClick={() => {
                    hapticSelect();
                    setGoalId(g.id);
                    setDirection("in");
                  }}
                >
                  {g.name}
                </button>
              ))}
            </div>
          </div>
        )}
        {goal && (
          <div className="goal-summary">
            <div className="goal-line">
              <b>{goal.name}</b>
              <span className="muted">{pct}%</span>
            </div>
            <span className="progress goal-progress">
              <span style={{ width: `${pct}%` }} />
            </span>
            <div className="goal-meta muted">
              {money(goal.saved, "UZS", lang)} / {money(goal.target, "UZS", lang)}
            </div>
          </div>
        )}
        {goal && goal.saved > 0 && (
          <Segmented
            value={direction}
            onChange={setDirection}
            options={[
              { value: "in", label: `＋ ${t("toGoal")}` },
              { value: "out", label: `− ${t("fromGoal")}` },
            ]}
          />
        )}
        <AmountField value={amount} onChange={setAmount} currency="UZS" lang={lang} quick={direction === "in" ? KID_QUICK : undefined} />
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <PrimaryAction label={direction === "in" ? t("toGoal") : t("fromGoal")} onClick={() => void submit()} busy={busy} />
      </div>
    </Sheet>
  );
}

// ───────────── Взрослым: «Ещё → Карманные деньги» ─────────────

/** Дети семьи: остаток и карманные; нажатие — кошелёк ребёнка. */
export function PocketKidsSheet({ lang, onClose }: { lang: Lang; onClose: () => void }) {
  const remote = useRemote("pocket:children", () => api.pocketChildren());
  const [picked, setPicked] = useState<PocketChildDto | null>(null);
  const kids = remote.data?.children ?? [];
  return (
    <Sheet title={`🧒 ${t("pocketMoney")}`} onClose={onClose}>
      {!remote.data && remote.loading ? (
        <div className="skeleton skeleton-block" />
      ) : kids.length === 0 ? (
        <EmptyState icon="🧒" title={t("noKids")} />
      ) : (
        <ul className="row-list">
          {kids.map((k) => (
            <li key={k.childId}>
              <button
                type="button"
                className="row row-btn"
                onClick={() => {
                  hapticSelect();
                  setPicked(k);
                }}
              >
                <span className="row-icon" aria-hidden="true">
                  🧒
                </span>
                <div className="row-body">
                  <div className="row-title">{k.name}</div>
                  <div className="row-meta">{k.allowance ? allowanceText(k.allowance, lang, false) : t("allowanceNone")}</div>
                </div>
                <div className={k.balance < 0 ? "row-end amount negative" : "row-end amount"}>{money(k.balance, "UZS", lang)}</div>
              </button>
            </li>
          ))}
        </ul>
      )}
      {picked && <ChildWalletSheet childId={picked.childId} name={picked.name} lang={lang} onClose={() => setPicked(null)} />}
    </Sheet>
  );
}

/** Кошелёк ребёнка глазами взрослого: выдать, карманные по расписанию, копилки, история. */
function ChildWalletSheet({ childId, name, lang, onClose }: { childId: string; name: string; lang: Lang; onClose: () => void }) {
  const remote = useRemote(`pocket:${childId}`, () => api.pocket(childId));
  const [sheet, setSheet] = useState<"give" | "allowance" | null>(null);
  const [goal, setGoal] = useState<GoalDto | null>(null);
  const w = remote.data;
  return (
    <Sheet title={`🧒 ${name}`} onClose={onClose}>
      {!w ? (
        <div className="skeleton skeleton-block" />
      ) : (
        <div className="form">
          <WalletHero wallet={w} lang={lang} inSheet />
          <ul className="row-list">
            <li>
              <button type="button" className="row row-btn" onClick={() => setSheet("allowance")}>
                <span className="row-icon" aria-hidden="true">
                  🗓
                </span>
                <div className="row-body">
                  <div className="row-title">{t("allowanceTitle")}</div>
                  <div className="row-meta">{w.allowance ? allowanceSchedule(w.allowance, lang) : t("allowanceNone")}</div>
                </div>
                <span className="chevron" aria-hidden="true">
                  ›
                </span>
              </button>
            </li>
          </ul>
          {w.goals.length > 0 && (
            <>
              <div className="section-title">🐷 {t("goalsTab")}</div>
              <GoalList goals={w.goals} lang={lang} onPick={setGoal} hint={paceHint(w, lang)} />
            </>
          )}
          <PocketHistory wallet={w} lang={lang} inSheet />
          <PrimaryAction label={`💰 ${t("giveBtn")}`} onClick={() => setSheet("give")} />
        </div>
      )}
      {sheet === "give" && <GiveSheet childId={childId} name={name} lang={lang} onClose={() => setSheet(null)} />}
      {sheet === "allowance" && w && <AllowanceSheet childId={childId} allowance={w.allowance} lang={lang} onClose={() => setSheet(null)} />}
      {goal && <ContributeSheet goal={goal} lang={lang} onClose={() => setGoal(null)} />}
    </Sheet>
  );
}

/** Выдать карманные сейчас: расход семьи «Дети» и деньги в кошельке. */
function GiveSheet({ childId, name, lang, onClose }: { childId: string; name: string; lang: Lang; onClose: () => void }) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (busy) return;
    const value = parseAmount(amount);
    if (!value) return setError(t("fillAmount"));
    setBusy(true);
    setError(null);
    try {
      await api.pocketGive(childId, value, note.trim() || null);
      hapticTap();
      toast(t("givenOk"));
      invalidateRemote();
      onClose();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  return (
    <Sheet title={`💰 ${t("giveTitle")} · ${name}`} onClose={onClose}>
      <div className="form">
        <AmountField value={amount} onChange={setAmount} currency="UZS" lang={lang} quick={GIVE_QUICK} />
        <label className="field">
          <span>{t("comment")}</span>
          <input className="input" value={note} maxLength={100} onChange={(e) => setNote(e.target.value)} />
        </label>
        <p className="hint">{t("giveHint")}</p>
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <PrimaryAction label={t("giveBtn")} onClick={() => void submit()} busy={busy} />
      </div>
    </Sheet>
  );
}

/** Карманные каждую неделю: сумма и день; «Выключить» — убрать расписание. */
function AllowanceSheet({ childId, allowance, lang, onClose }: { childId: string; allowance: PocketAllowanceDto | null; lang: Lang; onClose: () => void }) {
  const [amount, setAmount] = useState(allowance ? String(allowance.amount) : "");
  const [weekday, setWeekday] = useState(allowance?.weekday ?? 6);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = async (value: number | null) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.pocketAllowance(childId, value, weekday);
      hapticTap();
      toast(value === null ? t("allowanceOffOk") : t("allowanceSaved"));
      invalidateRemote();
      onClose();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  const submit = () => {
    const value = parseAmount(amount);
    if (!value) return setError(t("fillAmount"));
    void send(value);
  };

  return (
    <Sheet title={`🗓 ${t("allowanceTitle")}`} onClose={onClose}>
      <div className="form">
        <AmountField value={amount} onChange={setAmount} currency="UZS" lang={lang} quick={[10_000, 20_000, 50_000]} />
        <div className="field">
          <span>{t("allowanceDay")}</span>
          <div className="chips weekday-chips" role="radiogroup" aria-label={t("allowanceDay")}>
            {[1, 2, 3, 4, 5, 6, 7].map((d) => (
              <button
                key={d}
                type="button"
                role="radio"
                aria-checked={d === weekday}
                className={d === weekday ? "chip active" : "chip"}
                onClick={() => {
                  hapticSelect();
                  setWeekday(d);
                }}
              >
                {weekdayShort(d)}
              </button>
            ))}
          </div>
        </div>
        <p className="hint">{t("allowanceHint")}</p>
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        {allowance && (
          <button type="button" className="btn danger btn-block" disabled={busy} onClick={() => void send(null)}>
            {t("allowanceTurnOff")}
          </button>
        )}
        <PrimaryAction label={t("save")} onClick={submit} busy={busy} />
      </div>
    </Sheet>
  );
}
